import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, ValidationRule, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreateTicketFormValues, EditTicketFormValues } from '../../../types/form';
import { channelType } from '../../model/types';
import { generateTicketFormNameForStorage, getSelectedTabKeyFromUrl } from '../../store/childStore/tabStore';
import { useGSocialStatus } from '../../store/provider/GSocialStatusProvider';
import { defineMode } from '../../utils/forms/formHelpers';
import GrispiInputContainer from '../GrispiInput/GrispiInputContainer';
import GrispiTextAreaInput from '../GrispiInput/components/GrispiTextAreaInput';
import QuillGrispiRichTextEditor from '../QuillGrispiRichTextEditor';
import { UploadControllerProps } from '../QuillGrispiRichTextEditor/components/UploadController';
import { GrispiFile } from '../UploadFilesList';
import { isChannelGSocial } from './CommentTypeSelect';
import MentionedUserController from './MentionedUserController';

interface Props {
    commentChannel: channelType;
    publicVisible: boolean;
}

export default function CommentInput({ commentChannel, publicVisible }: Props) {
    const { t } = useTranslation('forms');
    const { t: tTickets } = useTranslation('tickets');
    const { asPath } = useRouter();
    const prevChannel = useRef<channelType>(commentChannel);
    const mode = defineMode(asPath);
    const isCreateMode = mode === 'create';

    const { getValues, clearErrors, setError, setValue } = useFormContext<CreateTicketFormValues>();

    const attachmentList = useWatch<CreateTicketFormValues, 'currentAttachments.attachments'>({
        name: 'currentAttachments.attachments'
    });

    const gSocialStatus = useGSocialStatus();

    useEffect(() => {
        const resetCommentBody =
            (isChannelGSocial(prevChannel.current) && commentChannel === 'WEB') ||
            (isChannelGSocial(commentChannel) && prevChannel.current !== commentChannel);

        resetCommentBody && clearErrors('comment.body');
        resetCommentBody && setValue('comment.body', null);

        prevChannel.current = commentChannel;
    }, [clearErrors, commentChannel, setValue]);

    const setAttachmentList = useCallback(
        (value: CreateTicketFormValues['currentAttachments']['attachments']) =>
            setValue('currentAttachments.attachments', value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
            }),
        [setValue]
    );
    const addAttachment = useCallback(
        (value: GrispiFile) =>
            setValue('currentAttachments.attachments', [...getValues('currentAttachments.attachments'), value], {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
            }),
        [getValues, setValue]
    );

    const onUploadSuccess: UploadControllerProps['onUploadSuccess'] = useCallback(
        (file, inlineImage = false) => {
            if (file && !inlineImage) {
                setValue('currentAttachments.attachments', [...getValues('currentAttachments.attachments'), file], {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true
                });
            }
            if (file && inlineImage) {
                setValue(
                    'currentAttachments.inlineImages',
                    [...getValues('currentAttachments.inlineImages'), { id: file.id, src: file.objectUrl }],
                    {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true
                    }
                );
            }

            clearErrors('comment.body');
        },
        [clearErrors, getValues, setValue]
    );

    const onUploadError: UploadControllerProps['onUploadError'] = useCallback(() => {
        const errorMessage = t('attachment', { context: 'failed' });
        setError('comment.body', { message: errorMessage });
    }, [setError, t]);

    return (
        <div className={clsx(!publicVisible && 'internal-style')}>
            <MentionedUserController />
            <Controller<CreateTicketFormValues | EditTicketFormValues, 'comment.body'>
                name="comment.body"
                render={({ field: { value, onChange }, fieldState: { error } }) => {
                    return (
                        <GrispiInputContainer noLabel errorMessage={error?.message}>
                            {isChannelGSocial(commentChannel) ? (
                                <GrispiTextAreaInput
                                    antdProps={{
                                        allowClear: false,
                                        autoSize: { minRows: 5, maxRows: 6 },
                                        status: error?.message ? 'error' : undefined
                                    }}
                                    containerProps={{
                                        noLabel: true,
                                        extra: gSocialStatus === 'inactive' ? tTickets('gSocialWarning') : undefined
                                    }}
                                    disabled={gSocialStatus === 'inactive'}
                                    onChange={onChange}
                                    value={value}
                                />
                            ) : (
                                <QuillGrispiRichTextEditor
                                    error={!!error?.message}
                                    internalView={!publicVisible}
                                    onChange={onChange}
                                    eventListenerKey={generateTicketFormNameForStorage(
                                        getSelectedTabKeyFromUrl(asPath)
                                    )}
                                    value={value}
                                    uploadProps={{
                                        attachmentList,
                                        setAttachmentList,
                                        addAttachment,
                                        onUploadSuccess,
                                        onUploadError,
                                        internalView: !publicVisible
                                    }}
                                    showTranslate
                                    showSignature
                                />
                            )}
                        </GrispiInputContainer>
                    );
                }}
                {...(isCreateMode
                    ? {
                          rules: { required: t('form.required') }
                      }
                    : {
                          rules: {
                              required: attachmentList.length > 0 ? t('form.required') : false,
                              maxLength: isChannelGSocial(commentChannel)
                                  ? {
                                        value: 280,
                                        message: t('form.validation.maxLength', { maxLength: 280 })
                                    }
                                  : ({} as ValidationRule<number>)
                          }
                      })}
            />
        </div>
    );
}
